<template>
<div class="hm-folio-list">
  <div class="hm-folio-list__grid hm-folio-list__grid--head">
    <span class="hm-folio-list__grid-head-name">Date</span>
    <span class="hm-folio-list__grid-head-name">Type</span>
    <span class="hm-folio-list__grid-head-name">Details</span>
    <span class="hm-folio-list__grid-head-name">Note</span>
    <span class="hm-folio-list__grid-head-name">Debit</span>
    <span class="hm-folio-list__grid-head-name">Credit</span>
  </div>
  <div v-for="item in transactions" :key="item.id" class="hm-folio-list__grid">
    <HMTooltip>
      <template #trigger>{{ formatDate(item.created_at) }}</template>
      <template #content>{{ formatTime(item.created_at) }}</template>
    </HMTooltip>
    <span class="hm-folio-list__grid-type">{{ item.category.name }}</span>
    <span>{{ item.details?.name || '-' }}</span>
    <span>{{ item.notes || '-' }}</span>
    <span class="hm-folio-list__grid-credit">{{ item.type.code !== 'credit' ? `${formatMoney(item.amount)}$` : '' }}</span>
    <span class="hm-folio-list__grid-debit">
      <template v-if="item.type.code === 'credit'">
        <template v-if="!item.details?.currency">{{ item.type.code === 'credit' ? `${formatMoney(item.amount)}$` : '' }}</template>
        <template v-else>
          {{ `${formatMoney(item.details.amount)} ${item.details.currency}` }}
          <span class="hm-folio-list__grid-debit-usd">{{ `${formatMoney(item.amount)}$` }}</span>
        </template>
      </template>
    </span>
    <span>
      <HMKebabMenu v-if="item.voided === false && isGeneralManager" :items="roomMenuItems" :context="item" toLeft />
    </span>
  </div>
  <modal
    :adaptive="true"
    :clickToClose="false"
    :height="'auto'"
    :reset="true"
    :scrollable="true"
    :width="400"
    name="moveTransaction"
  >
    <div class="popup-new">
      <button class="popup-new__close" type="button" @click="$modal.hide('moveTransaction')"></button>
      <div class="popup-new__title">Move Transaction</div>
      <div class="popup-new__body">
        <app-cells>
          <app-radio
            v-for="item in modal_type_options"
            :key="item.code"
            v-model="modal_type"
            :checked="modal_type"
            :value="item.code"
            name="modalType"
          >
            <template #label>{{ item.name }}</template>
          </app-radio>
        </app-cells>
        <form @submit.prevent="onCheckForm">
          <template v-if="modal_type === 'reservation'">
            <app-form-field label="Reservation" required :marginBottom="20">
              <app-select
                @input="onReservationsSelected"
                @search="onReservationsSearch"
                v-model="form.reservation"
                :options="reservations_options"
                :display-field="option => `${option.group_name} (${formatDate(option.start_date)} - ${formatDate(option.end_date)})`"
                :clearable="false"
                :filterBy="() => true"
                :calculate-position="withPopper"
                append-to-body
                label="group_name"
                class="family"
                placeholder="Reservation"
                :error="$v.form.reservation.$error"
              />
              <template #error>
                <div v-if="$v.form.reservation.$dirty && !$v.form.reservation.required">Required field</div>
              </template>
            </app-form-field>
            <app-form-field label="Room" required :marginBottom="20">
              <app-select
                v-model="form.reservation_room"
                :options="reservation_room_options"
                :clearable="false"
                :readonly="!reservation_room_options.length"
                :display-field="option => `${option.room.short_name} (${formatDate(option.short_room.check_in)} - ${formatDate(option.short_room.check_out)})`"
                :get-option-label="item => item.room.short_name"
                :calculate-position="withPopper"
                append-to-body
                class="family"
                placeholder="Room"
                :error="$v.form.reservation_room.$error"
              />
              <template #error>
                <div v-if="$v.form.reservation_room.$dirty && !$v.form.reservation_room.required">Required field</div>
              </template>
            </app-form-field>
          </template>
          <template v-else>
            <app-form-field label="House Account" required :marginBottom="20">
              <app-select
                @search="onAccountSearch"
                v-model="form.house_account"
                :options="account_options"
                :clearable="false"
                :calculate-position="withPopper"
                append-to-body
                class="family"
                label="account_name"
                placeholder="House Account"
                :error="$v.form.house_account.$error"
              />
              <template #error>
                <div v-if="$v.form.house_account.$dirty && !$v.form.house_account.required">Required field</div>
              </template>
            </app-form-field>
          </template>
          <app-cells :indent="false" position="center">
            <HMButton :loading="isSubmittingForm"><span>Save</span></HMButton>
          </app-cells>
        </form>
      </div>
    </div>
  </modal>
</div>
</template>

<script>
import {
  postHotelManagementTransactionsVoid,
  getHotelManagementReservationsV2,
  getHotelManagementReservationsRooms,
  getHotelManagementHouseAccountsV2,
  postHotelManagementTransactionsMove
} from '@/http'
import HMTooltip from '@/views/hotel-management/reservations/components/HMTooltip.vue'
import HMKebabMenu from '@/components/HMKebabMenu.vue'
import { debounce } from 'lodash'
import { required, requiredIf } from 'vuelidate/lib/validators'
import HMButton from '@/views/hotel-management/reservations/components/HMButton.vue'
import { createPopper } from '@popperjs/core'

export default {
  name: 'HMFolioList',
  props: {
    transactions: {
      type: Array,
      required: true,
    }
  },
  components: {HMButton, HMKebabMenu, HMTooltip},
  data() {
    return {
      modal_type: 'reservation',
      modal_type_options: [
        {name: 'Reservation', code: 'reservation'},
        {name: 'House Account', code: 'account'},
      ],
      form: {},
      reservations_options: [],
      reservation_room_options: [],
      account_options: [],
      isSubmittingForm: false,
      placement: 'bottom',
      transaction_to_action: null,
    }
  },
  validations: {
    form: {
      reservation: {
        required: requiredIf(function() {
          return this.modal_type === 'reservation'
        })
      },
      reservation_room: {
        required: requiredIf(function() {
          return this.modal_type === 'reservation'
        })
      },
      house_account: {
        required: requiredIf(function() {
          return this.modal_type === 'account'
        })
      },
    }
  },
  computed: {
    roomMenuItems() {
      return [
        {
          label: 'Void',
          icon: 'close.svg',
          onClick: this.onVoid,
        },
        {
          label: 'Move',
          icon: 'change-menu.svg',
          onClick: this.onMove,
        },
      ]
    },
    isGeneralManager() {
      return this.$store.state.userGroups.map(item => item.code).includes('general_manager')
    },
  },
  methods: {
    onCheckForm() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendForm()
      }
    },
    sendForm() {
      this.isSubmittingForm = true
      let data = {}
      if (this.modal_type === 'reservation') {
        data = {
          reservation: this.form.reservation.id,
          reservation_room: this.form.reservation_room.id,
        }
      } else {
        data = {
          house_account: this.form.house_account.id
        }
      }
      postHotelManagementTransactionsMove(this.transaction_to_action, data)
        .finally(() => { this.isSubmittingForm = false })
        .then(() => {
          this.$emit('postVoid')
          this.$modal.hide('moveTransaction')
        })
    },
    onReservationsSelected() {
      this.form.reservation_room = null
      getHotelManagementReservationsRooms({reservation: this.form.reservation.id})
        .then(response => {
          this.reservation_room_options = response.data
        })
    },
    onVoid(transaction) {
      postHotelManagementTransactionsVoid(transaction.id)
        .then(() => {
          this.$emit('postVoid')
        })
    },
    onMove(transaction) {
      this.transaction_to_action = transaction.id
      this.form = {
        reservation_room: null,
      }
      this.modal_type = 'reservation'
      this.reservation_room_options = []
      this.$v.form.$reset()
      this.$modal.show('moveTransaction')
    },
    formatMoney(num) {
      return `${new Intl.NumberFormat('en-US').format(num)}`
    },
    formatDate(iso) {
      if (!iso) return ''
      const d = new Date(iso)
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      return `${ d.getDate() } ${ months[d.getMonth()] }, ${ d.getFullYear() }`
    },
    formatTime(iso) {
      if (!iso) return ''
      return iso.slice(11, 16)
    },
    onReservationsSearch(search, loading) {
      loading(true)
      this.searchReservations(loading, search, this)
    },
    searchReservations: debounce((loading, search, vm) => {
      if (!search) {
        loading(false)
        vm.reservations_options = []
      } else {
        getHotelManagementReservationsV2({search}).then(response => {
          vm.reservations_options = response.data
          loading(false)
        })
      }
    }, 350),
    onAccountSearch(search, loading) {
      loading(true)
      this.searchAccount(loading, search, this)
    },
    searchAccount: debounce((loading, search, vm) => {
      if (!search) {
        loading(false)
        vm.account_options = []
      } else {
        getHotelManagementHouseAccountsV2({search}).then(response => {
          vm.account_options = response.data
          loading(false)
        })
      }
    }, 350),
    withPopper (dropdownList, component, {width}) {
      dropdownList.style.width = width
      createPopper(component.$refs.toggle, dropdownList, {
        placement: this.placement,
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, 0],
              adaptive: true
            }
          },
          {
            name: 'toggleClass',
            enabled: true,
            phase: 'write',
            fn({state}) {
              component.$el.classList.toggle('drop-up', state.placement === 'top')
            },
          },
        ]
      })
    }
  }
}
</script>

<style scoped>
.hm-folio-list__grid {
  display: grid;
  grid-template-columns: 120px 140px 220px 1fr 90px 90px 32px;
  gap: 10px;
  align-items: center;
  height: 50px;
  padding: 0 20px 0 30px;
  border-bottom: 1px solid #E7E7E7;
  color: #535353;
  font-size: 14px;
  font-family: 'CoreSansCR', sans-serif;
  line-height: normal;

  &:nth-child(even) {
    background-color: #FCFCFC;
  }
}

.hm-folio-list__grid--head {
  height: 30px;
  border-bottom: none;
  background-color: #F6F6F6;
}

.hm-folio-list__grid-head-name {
  color: #666;
  font-family: 'CoreSansCR', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: normal;
}

.hm-folio-list__grid-type {
  color: #000;
  font-size: 14px;
  font-weight: 500;
  line-height: normal;
}

.hm-folio-list__grid-debit,
.hm-folio-list__grid-credit{
  font-size: 14px;
  font-weight: 500;
  line-height: normal;
}

.hm-folio-list__grid-debit {
  color: #468832;
}

.hm-folio-list__grid-debit-usd {
  display: block;
  color: #000;
}

.hm-folio-list__grid-credit {
  color: #A64833;
}
</style>